/**
 * Query-language join wiring — PURE (no dashboard/React/network imports).
 *
 * cluster/identity query clauses reference fields on a joined alias (ec / du);
 * the join hop has to exist on the spec root before the planner sees those
 * FieldRefs. These mirror the child JoinSteps buildPromptRootAndFilter emits.
 *
 * Lives in its own pure module (not queryLangCompile.js, which imports the dim
 * adapters → shell → `window`) so kernelScope.js can attach query joins in a
 * node-env import without pulling that chain.
 *
 * The join MODE is LEFT (not the scope path's INNER): a query like
 * `agent:x OR identity:y` must keep prompts with no assignment so the OR can
 * match the agent branch. Every surface that adds query joins (getTraces,
 * getOverviewMetrics, the activity bundle, scoped kernels) goes through
 * ensureQueryJoins, so the join mode stays consistent across all of them.
 */

function clusterJoinStep() {
  return {
    edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
    mode: "JOIN_MODE_LEFT",
    target: { alias: "ec", entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER" },
  };
}

// PROMPT → ENDPOINT (alias `ep`) on endpoint_id, for hostname matching. `ep`,
// not `e` — `e` is the EVENT root in the trace-list spec.
function endpointHostJoinStep() {
  return {
    edge: "EDGE_TYPE_PROMPT_ON_ENDPOINT",
    mode: "JOIN_MODE_LEFT",
    target: { alias: "ep", entity: "ENTITY_TYPE_ENDPOINT" },
  };
}

function identityJoinStep() {
  return {
    edge: "EDGE_TYPE_PROMPT_HAS_USER_ASSIGNMENTS",
    mode: "JOIN_MODE_LEFT",
    target: {
      alias: "u",
      entity: "ENTITY_TYPE_ENDPOINT_USER_ASSIGNMENT",
      children: [{
        edge: "EDGE_TYPE_USER_ASSIGNMENT_HAS_DIRECTORY_USER",
        mode: "JOIN_MODE_LEFT",
        target: { alias: "du", entity: "ENTITY_TYPE_DIRECTORY_USER" },
      }],
    },
  };
}

// Find the EntityStep with the given alias anywhere in the tree (root + every
// join target). Returns the step node (live reference) or null.
function findStepByAlias(root, alias) {
  if (!root || typeof root !== "object") return null;
  if (root.alias === alias) return root;
  for (const child of root.children ?? []) {
    const hit = findStepByAlias(child?.target, alias);
    if (hit) return hit;
  }
  return null;
}

// Collect every alias present in the tree.
function collectAliases(root, out = new Set()) {
  if (!root || typeof root !== "object") return out;
  if (root.alias) out.add(root.alias);
  for (const child of root.children ?? []) collectAliases(child?.target, out);
  return out;
}

/**
 * Return a clone of `root` with the join hops required by `joins` attached to
 * the PROMPT step (alias `p`) — added only if their target alias isn't already
 * present (a spec may already carry `ec`). Recognised joins:
 *   "cluster"        → PROMPT → ec
 *   "endpoint_host"  → PROMPT → ENDPOINT (ep)   (hostname matching)
 *   "identity"       → PROMPT → u → du
 * Throws if there's no `p` step to hang them on (the query language is
 * PROMPT-scoped).
 */
export function ensureQueryJoins(root, joins) {
  if (!joins?.length) return root;
  const clone = structuredClone(root);
  const promptStep = findStepByAlias(clone, "p");
  if (!promptStep) {
    throw new Error("ensureQueryJoins: spec has no PROMPT step (alias 'p') to attach joins to");
  }
  const present = collectAliases(clone);
  promptStep.children = promptStep.children ? [...promptStep.children] : [];
  if (joins.includes("cluster") && !present.has("ec")) promptStep.children.push(clusterJoinStep());
  if (joins.includes("endpoint_host") && !present.has("ep")) promptStep.children.push(endpointHostJoinStep());
  if (joins.includes("identity") && !present.has("du")) promptStep.children.push(identityJoinStep());
  return clone;
}
